import { Controller } from "@hotwired/stimulus";

// Live preview on the LtS dues form: shows the tier that the entered amount
// maps to and whether it is an upgrade, downgrade, or no change versus the
// family's current tier. Tiers are runtime-defined (LifetimeSupporterTier),
// keyed here by tier id. The server re-derives the tier from the amount on
// submit; this is display-only.
export default class extends Controller {
  static targets = ["amount", "preview"];
  static values = {
    tiers: Object, // { "<id>": { amount: 365, name: "Lifetime Supporter" } }
    currentTier: Number,
    currentTierName: String,
    minimumAmount: Number,
  };

  connect() {
    this.markSelectedBoxes();
    this.update();
  }

  // Tier boxes backed by native radio inputs: the browser enforces single
  // selection natively, so here we only fill the amount input from the
  // chosen tier and refresh the selected styling + preview.
  selectTier(event) {
    const radio = event.target;
    const info = this.tiersValue[radio.value];
    if (radio.checked && info) {
      this.amountTarget.value = info.amount;
    }
    this.markSelectedBoxes();
    this.update();
  }

  markSelectedBoxes() {
    this.tierRadios().forEach((radio) => {
      radio.closest(".lts-tier-box")?.classList.toggle(
        "lts-tier-box-selected",
        radio.checked,
      );
    });
  }

  tierRadios() {
    return Array.from(
      this.element.querySelectorAll(".lts-tier-radio"),
    );
  }

  update() {
    const amount = parseFloat(this.amountTarget.value);
    if (isNaN(amount) || amount <= 0) {
      this.previewTarget.innerHTML = "";
      return;
    }
    if (amount < this.minimumAmountValue) {
      this.previewTarget.innerHTML =
        `<span class="text-danger">Amount must be at least $${this.minimumAmountValue}.</span>`;
      return;
    }
    const resultTier = this.tierForAmount(amount);
    const name = this.tiersValue[resultTier].name;
    const currentTier = String(this.currentTierValue);
    let change;
    if (String(resultTier) === currentTier) {
      change = "no change to your current tier";
    } else if (this.tierRank(resultTier) > this.tierRank(currentTier)) {
      change = `<strong>an upgrade</strong> from ${this.currentTierNameValue}`;
    } else {
      change = `<strong>a downgrade</strong> from ${this.currentTierNameValue}`;
    }
    this.previewTarget.innerHTML =
      `Paying <strong>$${amount.toFixed(2)}</strong> sets your tier to ` +
      `<strong>${name}</strong> &mdash; ${change}.`;
  }

  // Tiers ordered by threshold ascending; highest tier at or below the amount.
  tierForAmount(amount) {
    let result = null;
    for (const [tier, info] of this.sortedTiers()) {
      if (info.amount <= amount) result = tier;
    }
    return result;
  }

  tierRank(tier) {
    return this.sortedTiers().findIndex(([t]) => t === String(tier));
  }

  sortedTiers() {
    return Object.entries(this.tiersValue).sort(
      (a, b) => a[1].amount - b[1].amount,
    );
  }
}
